/**
 * Reporting-unit-scoped option-set helpers — Doc-only path.
 *
 * Used at pipeline stages where no Building is available (e.g. Plan Library reducer init).
 * For Building-based filters/collectors, see `reportingUnitOptionSets`.
 */
import type { Doc } from "building-types/doc";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { buildBuildingTree, type ComponentInstanceTree, type InstanceIdPath } from "doc/options/componentInstanceTree";
import {
  combineFilters,
  createActivePathFilter,
  createReportingUnitFilter,
  getChildrenToRender,
  subtreeMatches,
} from "lib/options/instanceTreeFiltering";

/**
 * Collects component instance IDs for a reporting unit. Doc-only alternative to the
 * Building-based collectors in reportingUnitOptionSets.ts — used at pipeline stages
 * where no Building is available (e.g. Plan Library reducer initialization).
 */
function collectInstanceIdsForUnit(doc: Doc, reportingUnitId: string): Set<string> {
  const tree = buildBuildingTree(doc);
  const maybeFilter = createReportingUnitFilter<ComponentInstanceTree<string>>(reportingUnitId);
  assertIsNotNullOrUndefined(maybeFilter);
  const filter = maybeFilter;

  const instanceIds = new Set<string>();

  function visit(node: ComponentInstanceTree<string>): void {
    if (filter.matches(node)) {
      instanceIds.add(node.id);
    }
    for (const child of node.children) {
      if (subtreeMatches(child, filter)) {
        visit(child);
      }
    }
  }

  for (const child of tree.children) {
    if (subtreeMatches(child, filter)) {
      visit(child);
    }
  }

  return instanceIds;
}

/**
 * Filters a table of option sets down to archetypes that have instance option sets in the
 * given reporting unit (excludes building-level options). Doc-based — for pipelines that
 * don't have a Building yet.
 */
export function filterToUnitLevelOptionSets(
  optionSets: Table<DocOptionSet>,
  doc: Doc,
  reportingUnitId: string,
): Table<DocOptionSet> {
  const unitInstanceIds = collectInstanceIdsForUnit(doc, reportingUnitId);

  return Object.fromEntries(
    Object.entries(optionSets).filter(([optionSetId]) => {
      for (const instanceOptionSet of Object.values(doc.instanceOptionSets)) {
        if (
          instanceOptionSet.archetypeId === optionSetId &&
          unitInstanceIds.has(instanceOptionSet.componentInstanceId)
        ) {
          return true;
        }
      }
      return false;
    }),
  );
}

/**
 * Filters a record keyed by option-set ID to the active instances in one reporting unit.
 * This is the Doc-only counterpart to the Building-based `filterOptionsToReportingUnit`.
 */
export function filterOptionsToReportingUnitFromDoc<T>(
  record: Record<string, T>,
  doc: Doc | undefined,
  activeInstancePaths: readonly InstanceIdPath[],
  selectedReportingUnitId: string | undefined,
): Record<string, T> {
  if (!doc || !selectedReportingUnitId) {
    return record;
  }

  const tree = buildBuildingTree(doc);
  const activePaths = new Set(activeInstancePaths);
  const activePathFilter =
    activePaths.size > 1 ? createActivePathFilter<ComponentInstanceTree<string>>(activePaths) : null;
  const filter = combineFilters(
    activePathFilter,
    createReportingUnitFilter<ComponentInstanceTree<string>>(selectedReportingUnitId),
  );
  const optionSetIds = new Set<string>();

  function visit(node: ComponentInstanceTree<string>): void {
    node.payload.forEach((optionSetId) => optionSetIds.add(optionSetId));
    getChildrenToRender(node, filter).forEach(visit);
  }

  // Reporting-unit saves intentionally exclude building-level option sets on the root node.
  getChildrenToRender(tree, filter).forEach(visit);
  return Object.fromEntries(Object.entries(record).filter(([optionSetId]) => optionSetIds.has(optionSetId)));
}
